import type { ComponentType, PropsWithChildren, ReactNode } from "react";
import type { FormikValues } from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import LoadingButton, { type LoadingButtonProps } from "@mui/lab/LoadingButton";
import type useForm from "hooks/useForm";
import { getFormInputProps } from "util/forms";
import { TriggerableDialog } from "ui/molecules/Dialog";

type Props<T extends FormikValues, TTextFieldComponentProps = TextFieldProps> = PropsWithChildren<{
    readonly form: ReturnType<typeof useForm<T>>;
    readonly TextFieldProps: TextFieldProps;
    readonly ButtonProps: LoadingButtonProps;
    readonly DialogActionProps: LoadingButtonProps;
    readonly renderDialogTitle: (form: ReturnType<typeof useForm<T>>) => ReactNode;
    readonly TextFieldComponent?: ComponentType<TTextFieldComponentProps>;
}>;

const AddBusinessUnit = <T extends FormikValues>({
    children,
    form,
    TextFieldProps,
    ButtonProps,
    DialogActionProps,
    renderDialogTitle,
    TextFieldComponent = TextField
}: Props<T>) => (
    <Box
        component='form'
        autoComplete='off'
        onSubmit={form.handleSubmit}
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1
        }}
    >
        <TextFieldComponent
            required
            fullWidth
            size='small'
            variant='outlined'
            {...TextFieldProps}
            {...getFormInputProps(TextFieldProps.name, form)}
            value={form.values[String(TextFieldProps.name)]}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
        />
        {children}
        <TriggerableDialog
            TriggerComponent={({ onClick }) => (
                <LoadingButton
                    {...ButtonProps}
                    disabled={!form.isValid || !form.dirty}
                    loading={form.isSubmitting}
                    onPointerDown={onClick}
                />
            )}
            DialogActionsComponent={({ onClose }) => (
                <>
                    <LoadingButton
                        variant='contained'
                        onPointerDown={() => (
                            form.submitForm()
                                .then(() => form.resetForm())
                                .finally(onClose)
                        )}
                        loading={form.isSubmitting}
                        {...DialogActionProps}
                    />
                    <Button
                        onPointerDown={onClose}
                    >
                        Cancel
                    </Button>
                </>
            )}
            ModalProps={{
                DialogProps: {
                    sx: {
                        '& .MuiDialog-container .MuiPaper-root': {
                            width: 'auto',
                            '& .MuiDialogContent-root': {
                                p: 0,
                                border: 'none'
                            }
                        },
                        '& .MuiDialogActions-root .MuiStack-root': {
                            justifyContent: 'center',
                        }
                    }
                },
                titleSlot: renderDialogTitle(form)
            }}
        />
    </Box>
);

export default AddBusinessUnit;
